/**
 * Revenue stream registry (mirrors Lambda finance/lib/revenueStreams.ts).
 *
 * Classify Complete transactions by TransactionReason (commercial mode),
 * not by inventory entityType.
 */

import * as APITypes from "~/API";

export type RevenueStreamId = "rent" | "stay" | "fee" | "other";

export type IncomeRevenueField =
  | "rentRevenue"
  | "stayRevenue"
  | "feeRevenue"
  | "otherRevenue";

export type ExpectedRevenueField = "expectedRent" | "expectedStayRevenue";

export type RevenueStreamDef = {
  id: RevenueStreamId;
  incomeField: IncomeRevenueField;
  reason: APITypes.TransactionReason;
  expectedField?: ExpectedRevenueField;
};

export const REVENUE_STREAMS: readonly RevenueStreamDef[] = [
  {
    id: "rent",
    incomeField: "rentRevenue",
    reason: APITypes.TransactionReason.Rent,
    expectedField: "expectedRent",
  },
  {
    id: "stay",
    incomeField: "stayRevenue",
    reason: APITypes.TransactionReason.Stay,
    expectedField: "expectedStayRevenue",
  },
  {
    id: "fee",
    incomeField: "feeRevenue",
    reason: APITypes.TransactionReason.Fee,
  },
  {
    id: "other",
    incomeField: "otherRevenue",
    reason: APITypes.TransactionReason.OtherPayment,
  },
];

const STREAM_BY_REASON = new Map<string, RevenueStreamDef>(
  REVENUE_STREAMS.map((stream) => [stream.reason, stream]),
);

export function classifyRevenueStream(
  reason: APITypes.TransactionReason | string | null | undefined,
): RevenueStreamDef | null {
  if (!reason) return null;
  return STREAM_BY_REASON.get(reason) ?? null;
}

export function incomeStreamAmount(
  income: APITypes.IncomeStatement | null | undefined,
  field: IncomeRevenueField,
): number {
  if (!income) return 0;
  return income[field] ?? 0;
}

export function applyTransactionToIncome(
  income: APITypes.IncomeStatement,
  transaction: Pick<APITypes.Transaction, "reason" | "amount">,
): RevenueStreamDef | null {
  const stream = classifyRevenueStream(transaction.reason);
  if (!stream) return null;
  income[stream.incomeField] =
    incomeStreamAmount(income, stream.incomeField) + transaction.amount;
  return stream;
}

export function sumIncomeRevenue(income: APITypes.IncomeStatement): number {
  let total = 0;
  for (const stream of REVENUE_STREAMS) {
    total += incomeStreamAmount(income, stream.incomeField);
  }
  return total;
}

export function accumulateIncomeRevenue(
  target: APITypes.IncomeStatement,
  source: APITypes.IncomeStatement,
): void {
  for (const stream of REVENUE_STREAMS) {
    target[stream.incomeField] =
      incomeStreamAmount(target, stream.incomeField) +
      incomeStreamAmount(source, stream.incomeField);
  }
}

export function expectedStreamAmount(
  report: Pick<APITypes.MonthlyStatusReport, "expectedRent"> & {
    expectedStayRevenue?: number | null;
  },
  field: ExpectedRevenueField,
): number {
  return report[field] ?? 0;
}

export function accumulateExpectedRevenue(
  target: APITypes.MonthlyStatusReport,
  source: APITypes.MonthlyStatusReport,
): void {
  for (const stream of REVENUE_STREAMS) {
    if (!stream.expectedField) continue;
    target[stream.expectedField] =
      expectedStreamAmount(target, stream.expectedField) +
      expectedStreamAmount(source, stream.expectedField);
  }
}

const EXPECTED_STAY_STATUSES = new Set<string>([
  APITypes.StayStatusType.Confirmed,
  APITypes.StayStatusType.CheckedIn,
  APITypes.StayStatusType.Completed,
]);

/** UTC calendar month (0-based) of a checkout instant. */
export function utcCheckoutMonthYear(
  checkOut: string,
): { month: number; year: number } | null {
  const ms = Date.parse(checkOut);
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms);
  return { month: d.getUTCMonth(), year: d.getUTCFullYear() };
}

/**
 * Confirmed/CheckedIn/Completed stays whose UTC checkout month is the
 * report month contribute Stay.totalAmount. No night proration in v1.
 */
export function expectedStayAmountForReportMonth(
  stay: Pick<APITypes.Stay, "status" | "checkOut" | "totalAmount">,
  month: number,
  year: number,
): number {
  if (!stay.status || !EXPECTED_STAY_STATUSES.has(stay.status)) return 0;
  if (!stay.checkOut) return 0;
  const checkout = utcCheckoutMonthYear(stay.checkOut);
  if (!checkout || checkout.month !== month || checkout.year !== year) {
    return 0;
  }
  return typeof stay.totalAmount === "number" && stay.totalAmount > 0
    ? stay.totalAmount
    : 0;
}
